/* eslint-disable arrow-body-style */
import React, { useState, useEffect, useContext, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Resizable } from 're-resizable';

// MUI
import { Box, Grid, Button, Collapse, Stack, Typography, Badge, SxProps, useMediaQuery, useTheme } from '@mui/material';

// Icons
import ToolSearchIcon from '@/icons/ToolSearchIcon';
import ToolRouteIcon from '@/icons/ToolRouteIcon';
import ToolSettingsIcon from '@/icons/ToolSettingsIcon';

// Custom Components
import { ClosePaneButton } from '@/ui/PublicPage/SidebarPanes';
import { ICommentPane, IInfoPane } from '@/@types/views/GIS/map';
import MapRoutingContext, { MapRoutingContexType } from '@/components/MapRoutingContext/MapRoutingContext';

const tabStyle = {
  borderColor: 'primary.main',
  borderStyle: 'solid',
  borderRightWidth: 1,
  borderLeftWidth: 1,
  borderTopWidth: 2,
  borderBottomWidth: 2,
  color: 'white',
  height: { xs: '60px', md: '100px' },
} as SxProps;

const Puller = () => {
  return (
    <Box
      sx={{
        height: '4px',
        width: '36px',
        position: 'absolute',
        left: 'calc( 50% - 18px )',
        top: '-7px',
        backgroundColor: '#888888',
        borderRadius: '5px',
      }}
    />
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value?: number | boolean;
}

function CustomTabPanelMobile(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="mobiletabpanel"
      id="mobile-tabpanel"
      aria-labelledby={`mobile-tab-${index}`}
      {...other}
      height="100%"
      sx={{
        display: value !== index ? 'none' : 'block',
      }}
      overflow={index === 0 ? 'hidden' : 'auto'}
    >
      {children}
    </Box>
  );
}

function CustomTabPanelDesktop(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="desktoptabpanel"
      id="desktop-tabpanel"
      aria-labelledby={`desktop-tab-${index}`}
      {...other}
      height="100%"
      sx={{
        borderColor: 'primary.main',
        borderStyle: 'solid',
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderTopWidth: 0,
        borderLeftWidth: 0,
        backgroundColor: '#FAFAFA',
        overflowY: 'auto',
        display: value !== index ? 'none' : 'block',
      }}
    >
      <Box height="100%" width="100%">
        {children}
      </Box>
    </Box>
  );
}

type PublicNavBarType = {
  children?: ReactNode;
  infoPane?: IInfoPane;
  commentsPane?: ICommentPane;
  handleDrawerStageInParent: (stage: number) => void;
  handlePaneInParent: (pane: number | undefined) => void;
  handleOpenInParent: (open: boolean) => void;
  obilaznicePane: any;
  ldtPane: any;
};

function PublicNavBar(props: PublicNavBarType) {
  const mapRoutingContext = useContext(MapRoutingContext) as MapRoutingContexType;

  const { routingPoints } = mapRoutingContext;

  const { children, infoPane, commentsPane, handleDrawerStageInParent, handlePaneInParent, handleOpenInParent, obilaznicePane, ldtPane } =
    props;

  const [pane, setPane] = useState<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [drawerStage, setDrawerStage] = useState(1);
  const [animate, setAnimate] = useState(false);

  const [height, setHeight] = useState<number>(document.documentElement.clientHeight * 0.4);

  const { t } = useTranslation();

  const snapStages = [0, document.documentElement.clientHeight * 0.4, document.documentElement.clientHeight - 124];
  const skipStageDeltas = [
    snapStages[2] - snapStages[1] + document.documentElement.clientHeight * 0.1,
    snapStages[1] - snapStages[0] + document.documentElement.clientHeight * 0.1,
  ];

  const handleChange = (newPane: number) => {
    if (newPane === pane) {
      setOpen(false);
      setTimeout(() => {
        setPane(undefined);
      }, 300);
    } else {
      setOpen(true);
      setPane(newPane);
    }
  };

  useEffect(() => {
    if (infoPane !== undefined) {
      setPane(3);
      if (open === false) {
        setOpen(true);
      }
      if (drawerStage === 0) {
        setDrawerStage(1);
      }
    } else {
      setOpen(false);
      setPane(undefined);
    }
  }, [infoPane]);

  useEffect(() => {
    if (commentsPane !== undefined) {
      setPane(4);
      if (open === false) {
        setOpen(true);
      }
      if (drawerStage === 0) {
        setDrawerStage(1);
      }
    } else {
      setOpen(false);
      setPane(undefined);
    }
  }, [commentsPane]);

  useEffect(() => {
    if (routingPoints.length !== 0) {
      if (pane !== 0 && pane !== 5) {
        setPane(1);
      }
      if (open === false) {
        setOpen(true);
      }
      if (drawerStage === 0) {
        setDrawerStage(1);
      }
    }
  }, [routingPoints]);

  useEffect(() => {
    if (obilaznicePane !== undefined) {
      setPane(5);
      if (open === false) {
        setOpen(true);
      }
    }
  }, [obilaznicePane]);

  useEffect(() => {
    if (ldtPane !== undefined) {
      setPane(6);
      if (open === false) {
        setOpen(true);
      }
    }
  }, [ldtPane]);

  const handleToggleHidePane = (state: boolean) => {
    setOpen(state);
    setTimeout(() => {
      setPane(undefined);
    }, 300);
  };

  const handleDrawerStageChange = (deltaHeight: number) => {
    setAnimate(true);
    if (deltaHeight > 0 && drawerStage !== 2) {
      if (drawerStage === 0 && deltaHeight > skipStageDeltas[0]) {
        setDrawerStage(drawerStage + 2);
      } else if (deltaHeight >= document.documentElement.clientHeight * 0.1) {
        setDrawerStage(drawerStage + 1);
      }
    } else if (deltaHeight < 0 && drawerStage !== 0) {
      if (drawerStage === 2 && deltaHeight < -1 * skipStageDeltas[1]) {
        setDrawerStage(drawerStage - 2);
      } else if (deltaHeight <= document.documentElement.clientHeight * -0.1) {
        setDrawerStage(drawerStage - 1);
      }
    }
  };

  useEffect(() => {
    handleDrawerStageInParent(drawerStage);

    if (drawerStage === 0) {
      setOpen(false);
      setTimeout(() => {
        setPane(undefined);
        setHeight(snapStages[drawerStage]);
        setDrawerStage(1);
      }, 300);
    } else {
      setHeight(snapStages[drawerStage]);
    }
  }, [drawerStage]);

  useEffect(() => {
    handlePaneInParent(pane);
  }, [pane]);

  useEffect(() => {
    handleOpenInParent(open);
  }, [open]);

  return (
    <>
      <Box
        sx={{
          pointerEvents: open ? 'auto' : 'none',
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          bottom: { xs: 0, md: 'auto' },
          left: 0,
          width: { xs: '100%', md: '400px' },
          height: { xs: 'auto', md: '100%' },
        }}
        overflow={'hidden'}
        zIndex={1000}
      >
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Collapse in={open}>
            <Resizable
              style={{
                touchAction: 'none',
                zIndex: 1201,
                borderTop: '6px solid #fafafa',
                backgroundColor: '#fafafa',
                borderTopLeftRadius: drawerStage === 2 ? '0px' : '25px',
                borderTopRightRadius: drawerStage === 2 ? '0px' : '25px',
                paddingTop: '6px',
                transition: animate ? 'height 0.3s' : 'unset',
              }}
              size={{ width: '100%', height }}
              maxHeight={snapStages[2]}
              enable={{
                top: true,
                right: false,
                bottom: false,
                left: false,
                topRight: false,
                bottomRight: false,
                bottomLeft: false,
                topLeft: false,
              }}
              onResizeStop={(event, direction, elementRef, delta) => {
                handleDrawerStageChange(delta.height);
              }}
              onResizeStart={(event, direction, elementRef) => {
                setAnimate(false);
              }}
              handleStyles={{ top: { height: '25px' } }}
            >
              <Box position="relative">
                <Puller></Puller>
              </Box>

              {React.Children.map(children as React.ReactElement[], (child, i) => {
                const childWithProps = child ? React.cloneElement(child, { layoutMode: 'mobile' }) : null;
                return (
                  <CustomTabPanelMobile value={pane} index={i}>
                    {childWithProps}
                  </CustomTabPanelMobile>
                );
              })}
            </Resizable>
          </Collapse>
        </Box>
        <Grid container textAlign="center" zIndex={1202} sx={{ pointerEvents: 'auto' }}>
          {[0, 1, 2].map((x) => {
            return (
              <Grid key={x} size={{ xs: 4, sm: 4 }} bgcolor={pane === x ? '#DDDDDD' : '#FAFAFA'} sx={{ ...tabStyle }}>
                <Button
                  fullWidth
                  aria-label="search"
                  sx={{ height: '100%' }}
                  onClick={(evt) => {
                    handleChange(x);
                  }}
                >
                  <Stack
                    sx={{
                      position: 'relative',
                      top: { md: '-10px', xs: '0' },
                    }}
                    direction="column"
                    alignItems="center"
                  >
                    {x === 0 ? (
                      <ToolSearchIcon color="primary" fontSize="large" />
                    ) : x === 1 ? (
                      routingPoints.length === 0 ? (
                        <ToolRouteIcon color="primary" fontSize="large" />
                      ) : (
                        <Badge badgeContent={routingPoints.length} color="secondary" style={{ margin: 0, padding: 0 }}>
                          <ToolRouteIcon color="primary" fontSize="large" />
                        </Badge>
                      )
                    ) : x === 2 ? (
                      <ToolSettingsIcon color="primary" fontSize="large" />
                    ) : null}
                    <Typography
                      variant="body2"
                      sx={{ textTransform: 'capitalize', display: { xs: 'none', md: 'block' }, position: 'absolute', bottom: -20 }}
                    >
                      {x === 0 ? t('buttons.search') : x === 1 ? t('buttons.routing') : x === 2 ? t('buttons.layers') : null}
                    </Typography>
                  </Stack>
                </Button>
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Collapse orientation="horizontal" in={open}>
            <Box width="400px" sx={{ transition: 'all 0.5s' }} height="calc( 100vh - 160px )" position="relative" zIndex={2000}>
              {React.Children.map(children as React.ReactElement[], (child, i) => {
                const childWithProps = child ? React.cloneElement(child, { layoutMode: 'desktop' }) : null;
                return (
                  <CustomTabPanelDesktop value={pane} index={i}>
                    {childWithProps}
                  </CustomTabPanelDesktop>
                );
              })}
            </Box>
          </Collapse>
        </Box>
      </Box>
      <ClosePaneButton handleToggleHidePane={handleToggleHidePane} open={open} pane={pane} />
    </>
  );
}

export default PublicNavBar;
